import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataThresholdingIcon from '@mui/icons-material/DataThresholding';
import './TradeAnalytics.css';
import { usePeriod } from './PeriodContext';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

Date.prototype.getWeek = function () {
  const date = new Date(this.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNumber;
};

const TradeAnalytics = ({ userID, trades }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [journalEntries, setJournalEntries] = useState([]);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const processTrades = () => {
      const filteredEntries = trades.filter(entry => entry.status === (showMissedTrades ? 'Missed' : 'Closed'));
      setJournalEntries(filteredEntries);
    };

    processTrades();
  }, [userID, trades, showMissedTrades]);

  const filterEntries = (entries) => {
    const matchesPeriod = (entryDate) => {
      if (selectedPeriod === 'today') {
        return entryDate.toDateString() === new Date().toDateString();
      } else if (selectedPeriod === 'this-week') {
        return entryDate.getWeek() === new Date().getWeek() && entryDate.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'this-month') {
        return entryDate.getMonth() === new Date().getMonth() && entryDate.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'this-year') {
        return entryDate.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'pick-month') {
        return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
      } else if (selectedPeriod === 'pick-year') {
        return entryDate.getFullYear() === selectedYear;
      } else if (selectedPeriod === 'pick-month-year') {
        return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
      } else {
        return true; // all-time
      }
    };

    return entries.filter(entry => {
      const matchesTag = selectedTag === 'all' || 
        (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));
      return matchesPeriod(new Date(entry.date)) && matchesTag;
    });
  };

  const calculateAverageRiskAmount = () => {
    const filteredEntries = filterEntries(journalEntries);
    const riskAmounts = filteredEntries.map(entry => parseFloat(entry.riskAmount)).filter(riskAmount => !isNaN(riskAmount));
    const totalRiskAmount = riskAmounts.reduce((acc, curr) => acc + curr, 0);
    const averageRiskAmount = totalRiskAmount / riskAmounts.length;
    return isNaN(averageRiskAmount) ? 'N/A' : <span style={{ color: '#c62828' }}>${averageRiskAmount.toFixed(2)}</span>;
  };

  const calculateAverageTakeProfitPercentage = () => {
    const filteredEntries = filterEntries(journalEntries).filter(entry => !isNaN(entry.tp) && !isNaN(entry.entry) && !isNaN(entry.exit));
    const totalEntries = filteredEntries.length;
    if (totalEntries === 0) return 'N/A';

    let totalCorrectExits = 0;
    for (const entry of filteredEntries) {
      const tp = parseFloat(entry.tp);
      const exitPrice = parseFloat(entry.exit);
      if (exitPrice >= tp) {
        totalCorrectExits++;
      }
    }

    const percentageOfCorrectExits = (totalCorrectExits / totalEntries) * 100;
    return (
      <span style={{ color: percentageOfCorrectExits >= 50 ? '#388e3c' : '#c62828' }}>
        {`${percentageOfCorrectExits.toFixed(2)}%`}
      </span>
    );
  };

  const calculateAverageReturnPercentage = () => {
    const filteredEntries = filterEntries(journalEntries);
    const returnPercentages = filteredEntries
      .map(entry => {
        let returnPercentage = parseFloat(entry.returnPercentage);
        const returns = parseFloat(entry.returns);
        const isShort = entry.shortLong.toLowerCase() === 'short';
  
        // For short trades, ensure returnPercentage aligns with returns
        if (isShort && !isNaN(returns) && !isNaN(returnPercentage)) {
          // If returns are positive (profitable short), returnPercentage should be positive
          if (returns > 0 && returnPercentage < 0) {
            returnPercentage = Math.abs(returnPercentage);
          }
          // If returns are negative (losing short), returnPercentage should be negative
          else if (returns < 0 && returnPercentage > 0) {
            returnPercentage = -Math.abs(returnPercentage);
          }
        }
  
        return returnPercentage;
      })
      .filter(returnPercentage => !isNaN(returnPercentage));
  
    const totalReturnPercentage = returnPercentages.reduce((acc, curr) => acc + curr, 0);
    const averageReturnPercentage = totalReturnPercentage / returnPercentages.length;
    const color = averageReturnPercentage >= 0 ? '#388e3c' : '#c62828';
    return isNaN(averageReturnPercentage) ? 'N/A' : (
      <span style={{ color }}>{averageReturnPercentage.toFixed(2)}%</span>
    );
  };

  const calculateAverageRiskRewardRatio = () => {
    const filteredEntries = filterEntries(journalEntries);
    const totalRisk = filteredEntries.reduce((sum, trade) => sum + Math.abs(parseFloat(trade.entry) - parseFloat(trade.sl)), 0);
    const totalReward = filteredEntries.reduce((sum, trade) => sum + Math.abs(parseFloat(trade.tp) - parseFloat(trade.entry)), 0);
    const riskRewardRatio = totalRisk ? totalReward / totalRisk : 0;
    return isNaN(riskRewardRatio) ? 'N/A' : <span style={{ color: '' }}>{riskRewardRatio.toFixed(2)}</span>;
  };

  const calculateAverageTradesPerPeriod = () => {
    const filteredEntries = filterEntries(journalEntries);
    if (filteredEntries.length === 0) return { avgTradesPerDay: 'N/A', avgTradesPerWeek: 'N/A' };

    const tradingDays = new Set(
      filteredEntries.map(entry => new Date(entry.date).toDateString())
    );
    const totalTrades = filteredEntries.length;
    const daysTraded = tradingDays.size;

    const weekMap = new Map();
    filteredEntries.forEach(entry => {
      const date = new Date(entry.date);
      const weekKey = `${date.getFullYear()}-${date.getWeek()}`;
      weekMap.set(weekKey, (weekMap.get(weekKey) || 0) + 1);
    });

    const avgTradesPerActiveDay = totalTrades / daysTraded;
    const avgTradesPerActiveWeek = Array.from(weekMap.values())
      .reduce((sum, count) => sum + count, 0) / weekMap.size;

    return {
      avgTradesPerDay: avgTradesPerActiveDay.toFixed(2),
      avgTradesPerWeek: avgTradesPerActiveWeek.toFixed(2)
    };
  };

  const calculateAverageHoldingTime = () => {
    const filteredEntries = filterEntries(journalEntries);
    const holdingTimes = filteredEntries
      .map(entry => {
        if (entry.exitDate && entry.exitTime && entry.date && entry.entryTime) {
          const entryDateTime = new Date(`${entry.date}T${entry.entryTime}`);
          const exitDateTime = new Date(`${entry.exitDate}T${entry.exitTime}`);
          const diffInMillis = exitDateTime - entryDateTime;
          
          const hours = Math.floor(diffInMillis / (1000 * 60 * 60));
          const minutes = Math.floor((diffInMillis % (1000 * 60 * 60)) / (1000 * 60));
          return { hours, minutes };
        }
        return null;
      })
      .filter(holdingTime => holdingTime !== null);

    const totalHours = holdingTimes.reduce((acc, curr) => acc + curr.hours, 0);
    const totalMinutes = holdingTimes.reduce((acc, curr) => acc + curr.minutes, 0);
    const averageHours = totalHours / holdingTimes.length;
    const averageMinutes = totalMinutes / holdingTimes.length;

    if (isNaN(averageHours) || isNaN(averageMinutes)) {
      return 'N/A';
    } else {
      const formattedHours = Math.floor(averageHours);
      const formattedMinutes = Math.floor(averageMinutes);
      if (formattedHours === 0 && formattedMinutes === 0) {
        return 'N/A';
      } else if (formattedHours === 0) {
        return `${formattedMinutes} mins`;
      } else if (formattedMinutes === 0) {
        return `${formattedHours} hrs`;
      } else {
        return `${formattedHours} hrs ${formattedMinutes} mins`;
      }
    }
  };

  const { avgTradesPerDay, avgTradesPerWeek } = calculateAverageTradesPerPeriod();

  const tooltipContent = `The Key Trading Performance Metrics include: Risk Amount (average dollar risk per trade), TP Accuracy (percentage of trades hitting take profit), Return % (average profit or loss per trade), RR Ratio (average risk/reward ratio), Trading Frequency (average trades per day/week), and Hold Time (average trade duration) for ${showMissedTrades ? 'missed' : 'closed'} trades.`;

  return (
    <Card style={{ minHeight: '270px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <DataThresholdingIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Trade Analytics {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip 
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': {
                      color: theme.palette.background.paper,
                    }
                  }
                }
              }}
            >
              <InfoIcon 
                sx={{ 
                  ml: 1, 
                  fontSize: '1rem',
                  color: iconColor,
                  cursor: 'help'
                }} 
              />
            </Tooltip>
            <MDTypography variant="h4" fontFamily="inherit" style={{ visibility: 'hidden' }}>0</MDTypography>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox pb={2} px={2}>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Average Risk Amount:  <MDTypography component="span" variant="button" fontWeight="bold" color="error" fontFamily="inherit">{calculateAverageRiskAmount()}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            TP Accuracy:  <MDTypography component="span" variant="button" fontWeight="bold" color="success" fontFamily="inherit">{calculateAverageTakeProfitPercentage()}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Average Return %:  <MDTypography component="span" variant="button" fontWeight="bold" color="success" fontFamily="inherit">{calculateAverageReturnPercentage()}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Average RR Ratio:  <MDTypography component="span" variant="button" fontWeight="bold" color="info" fontFamily="inherit">{calculateAverageRiskRewardRatio()}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Average Trades P/Day:  <MDTypography component="span" variant="button" fontWeight="bold" color="info" fontFamily="inherit">{avgTradesPerDay}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Average Trades P/Week:  <MDTypography component="span" variant="button" fontWeight="bold" color="info" fontFamily="inherit">{avgTradesPerWeek}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Average Hold Time:  <MDTypography component="span" variant="button" fontWeight="bold" color="info" fontFamily="inherit">{calculateAverageHoldingTime()}</MDTypography>
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
};

TradeAnalytics.propTypes = {
  userID: PropTypes.string.isRequired,
  trades: PropTypes.array.isRequired,
};

export default TradeAnalytics;